import { useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { MRT_VisibilityState } from 'mantine-react-table';

interface UseCustomColumnVisibilityOptions {
  initialColumns: MRT_VisibilityState;
  conditionalColumns?: MRT_VisibilityState;
}

interface UseCustomColumnVisibilityResult {
  columnVisibility: MRT_VisibilityState;
  onColumnVisibilityChange: Dispatch<SetStateAction<MRT_VisibilityState>>;
}

export const useCustomColumnVisibility = ({
  initialColumns,
  conditionalColumns = {},
}: UseCustomColumnVisibilityOptions): UseCustomColumnVisibilityResult => {
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    () => ({
      ...initialColumns,
      ...conditionalColumns,
    })
  );
  const previousConditionalColumns = useRef(conditionalColumns);

  useEffect(() => {
    const changedColumns = Object.entries(conditionalColumns).filter(
      ([columnID, isVisible]) =>
        previousConditionalColumns.current[columnID] !== isVisible
    );

    previousConditionalColumns.current = conditionalColumns;
    if (changedColumns.length === 0) return;

    setColumnVisibility((currentVisibility) => {
      const nextVisibility = { ...currentVisibility };

      for (const [columnID, isVisible] of changedColumns) {
        nextVisibility[columnID] = isVisible;
      }

      return nextVisibility;
    });
  }, [conditionalColumns]);

  return {
    columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
  };
};
